import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import type { MessagingMetaData } from '@feature/cases/case_messaging_model';
import { quirk_iOSKeyboardLockBodyScroll } from '@module/quirk_ios_keyboard_lock_body_scroll';
import { useService } from '@visiba/instantiation';
import { usePresentation } from '@visiba/presentation';
import { ReactNode, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ChatInputArea } from './_chat_input_area.component';
import { ChatMessagesContainer } from './_chat_messages_container.component';
import { ObfuscateNameContext } from './_obfuscate_name.context';
import { ChatController } from './chat.controller';
import { ChatCommunicatorProtocol } from './chat_communicator_protocol';

interface Props {
	communicator: ChatCommunicatorProtocol;
	/**
	 * @deprecated
	 */
	connectedId: string;
	isReadyToJoin?: boolean;
	header?: ReactNode | ReactNode[];
	forceHideActions?: boolean;
	showTopBanner?: ReactNode;
	showBottomBanner?: ReactNode;
	issuesTrackedById?: string;
	onMarkAsCompleted?: () => void;
	onReceivedHistory?: () => void;
	onSendMessage?: () => void;
	obfuscateName?: boolean;
	messagingMetaData?: MessagingMetaData | null;
	onConnectionError?: (error: unknown) => void;
	onUnauthorized?: () => void;
	hideFileUpload?: boolean;
}

const elements = {
	container: styled.div`
		display: flex;
		flex-direction: column;
		height: 100%;
		flex: 1;
	`,
	header: styled.div`
		width: 100%;
	`,
};

export function Chat({
	communicator,
	connectedId,
	header,
	forceHideActions,
	showTopBanner,
	showBottomBanner,
	issuesTrackedById,
	onMarkAsCompleted,
	onReceivedHistory,
	onSendMessage,
	obfuscateName = false,
	messagingMetaData,
	isReadyToJoin,
	onConnectionError,
	onUnauthorized,
	hideFileUpload = false,
}: Props): JSX.Element {
	const controller = useService(ChatController);
	const presentation = usePresentation(controller.presentation);
	const analyticCtx = useAnalyticContext();
	const containerRef = useRef<HTMLDivElement>(null);
	const hasReadUntilData = messagingMetaData?.hasReadUntilTimestamp;

	function handleSend(message: string | null, fileTickets: string[]): void {
		controller.sendMessage(message, fileTickets);
		analyticCtx.trackEvent('User clicked on send message button');

		if (onSendMessage != null) {
			onSendMessage();
		}
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(
		function whenUserIsReadyToJoinRoom() {
			const readyToJoin = messagingMetaData != null || isReadyToJoin;

			if (readyToJoin) {
				controller.join(
					connectedId,
					{
						issuesTrackedById,
						hasReadUntilTimestamp: hasReadUntilData ?? null,
						onConnectionError,
						onUnauthorized,
					},
					communicator,
				);
			}
		},
		[controller, connectedId, hasReadUntilData, isReadyToJoin, issuesTrackedById, communicator, onConnectionError, onUnauthorized],
	);

	useEffect(
		function whenComponentUnmounts() {
			return () => {
				controller.terminate();
			};
		},
		[controller],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(
		function whenConnectedIdChanges() {
			return () => {
				controller.removeCurrentBridge();
			};
		},
		[controller, connectedId],
	);

	return (
		<ObfuscateNameContext.Provider value={obfuscateName}>
			<elements.container
				ref={containerRef}
				onBlur={() => {
					quirk_iOSKeyboardLockBodyScroll();
				}}
			>
				<elements.header>{header}</elements.header>
				<ChatMessagesContainer
					bridge={presentation.communicator}
					showLoader={!presentation.isReadyForCommunication}
					markAsCompleted={forceHideActions ?? false}
					topBanner={showTopBanner}
					bottomBanner={showBottomBanner}
					onMarkAsCompleted={onMarkAsCompleted}
					onReceivedHistory={onReceivedHistory}
					messagingMetaData={messagingMetaData}
				/>
				{!forceHideActions ? (
					<ChatInputArea
						connectedId={connectedId}
						fileUploadDispositionUri={communicator.fileUploadDispositionUri}
						onSend={handleSend}
						isReadyForCommunication={presentation.isReadyForCommunication}
						hideFileUpload={hideFileUpload}
					/>
				) : null}
			</elements.container>
		</ObfuscateNameContext.Provider>
	);
}
